import { useTheme } from "@emotion/react";
import { Box, Button, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchUsers,
  sendUserWelcomeEmail,
} from "reduxStore/actions/userActions";
import {
  EditOutlined,
  EmailOutlined,
  LockResetOutlined,
} from "@mui/icons-material";
import { setSnackbar } from "reduxStore/state";
import { sendPasswordResetEmail } from "reduxStore/actions/authActions";

export const useUserGrid = ({ handleEditOpen }) => {
  const { palette } = useTheme();
  const backgroundAlt = palette.background.alt;
  const primaryLight = palette.primary.light;
  const primaryDark = palette.primary.dark;

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("");

  const [searchInput, setSearchInput] = useState("");

  const dispatch = useDispatch();

  const users = useSelector((state) => state.users.data || []);
  const totalUsers = useSelector((state) => state.users.total || 0);

  const [passwordResetDialogOpen, setPasswordResetDialogOpen] = useState(false);
  const [selectedUserEmail, setSelectedUserEmail] = useState("");

  // State for welcome email dialog
  const [welcomeEmailDialogOpen, setWelcomeEmailDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedUserName, setSelectedUserName] = useState("");

  useEffect(() => {
    dispatch(
      fetchUsers({
        page,
        limit: pageSize,
        sort,
        search,
      })
    );
  }, [page, pageSize, sort, search, dispatch]);

  const handleSendPasswordReset = async () => {
    setPasswordResetDialogOpen(false);

    const success = await dispatch(sendPasswordResetEmail(selectedUserEmail));

    if (success) {
      dispatch(
        setSnackbar({
          open: true,
          message: `Password reset email sent to ${selectedUserEmail}`,
          severity: "success",
        })
      );
    } else {
      dispatch(
        setSnackbar({
          open: true,
          message: `Failed to send password reset email to ${selectedUserEmail}`,
          severity: "error",
        })
      );
    }
  };

  const handleSendWelcomeEmail = async () => {
    setWelcomeEmailDialogOpen(false);

    const result = await dispatch(sendUserWelcomeEmail(selectedUserId));

    if (result) {
      dispatch(
        setSnackbar({
          open: true,
          message: `Welcome email sent to ${selectedUserName}`,
          severity: "success",
        })
      );
    } else {
      dispatch(
        setSnackbar({
          open: true,
          message: `Failed to send welcome email to ${selectedUserName}`,
          severity: "error",
        })
      );
    }
  };

  const columns = [
    { field: "firstName", headerName: "First Name", flex: 1 },
    { field: "lastName", headerName: "Last Name", flex: 1 },
    { field: "email", headerName: "Email", flex: 1 },
    {
      field: "roles",
      headerName: "Roles",
      flex: 0.75,
      sortable: false,
      renderCell: (params) => {
        if (!params.row.roles || params.row.roles.length === 0) {
          return null;
        }

        const getInitials = (name) =>
          name
            .split(" ")
            .map((word) => word[0])
            .join("")
            .toUpperCase();

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center", // Ensures vertical centering
              justifyContent: "flex-start", // Aligns items to the left
              gap: "4px", // Adjusts spacing between role badges
              whiteSpace: "nowrap", // Prevents wrapping to new lines
              overflow: "hidden", // Ensures it doesn’t expand cell height
              textOverflow: "ellipsis", // Handles overflow text cleanly
              height: "100%", // Ensures it fills the row height properly
            }}
          >
            {params.row.roles.map((role, index) => (
              <Box
                key={`${role._id}-${index}`}
                sx={{
                  display: "flex",
                  alignItems: "center", // Centers the text inside the badge
                  justifyContent: "center",
                  px: "8px",
                  py: "4px",
                  borderRadius: "5px",
                  fontSize: "12px",
                  fontWeight: "bold",
                  backgroundColor: primaryDark,
                  color: primaryLight,
                  textAlign: "center",
                  minWidth: "28px", // Prevents shrinking too much
                  height: "24px", // Ensures badges are visually centered
                }}
              >
                {getInitials(role.name)}
              </Box>
            ))}
          </Box>
        );
      },
    },

    {
      field: "organisation",
      headerName: "Organisation",
      flex: 1,
      sortable: false,
      renderCell: (params) => params.row.organisation?.name,
    },
    {
      field: "edit",
      headerName: "Edit",
      flex: 0.5,
      sortable: false,
      renderCell: (params) => (
        <button
          onClick={() => handleEditOpen(params.row)} // Open the edit dialog with unit details
          style={{ backgroundColor: "transparent", border: "none" }}
        >
          <EditOutlined sx={{ color: primaryDark }} />
        </button>
      ),
    },
    {
      field: "resetPassword",
      headerName: "Reset Password",
      flex: 0.5,
      headerAlign: "center",
      align: "center",
      sortable: false,
      disableColumnMenu: true,
      renderHeader: () => (
        <Box
          display="flex"
          justifyContent="center"
          sx={{
            whiteSpace: "normal",
            textAlign: "center",
            wordBreak: "break-word",
          }}
        >
          <Typography variant="body2" fontWeight={500}>
            Reset Password
          </Typography>
        </Box>
      ),
      renderCell: (params) => (
        <Button
          onClick={() => {
            setSelectedUserEmail(params.row.email);
            setSelectedUserName(
              `${params.row.firstName} ${params.row.lastName}`
            );
            setPasswordResetDialogOpen(true);
          }}
          style={{ backgroundColor: "transparent", border: "none" }}
        >
          <LockResetOutlined sx={{ color: primaryDark }} />
        </Button>
      ),
    },
    {
      field: "sendWelcomeEmail",
      headerName: "Send Welcome Email",
      flex: 0.5,
      headerAlign: "center",
      align: "center",
      sortable: false,
      disableColumnMenu: true,
      renderHeader: () => (
        <Box
          display="flex"
          justifyContent="center"
          sx={{
            whiteSpace: "normal",
            textAlign: "center",
            wordBreak: "break-word",
          }}
        >
          <Typography variant="body2" fontWeight={500}>
            Send Welcome Email
          </Typography>
        </Box>
      ),
      renderCell: (params) => (
        <Button
          onClick={() => {
            setSelectedUserId(params.row._id);
            setSelectedUserEmail(params.row.email);
            setSelectedUserName(
              `${params.row.firstName} ${params.row.lastName}`
            );
            setWelcomeEmailDialogOpen(true);
          }}
          style={{ backgroundColor: "transparent", border: "none" }}
        >
          <EmailOutlined sx={{ color: primaryDark }} />
        </Button>
      ),
    },
  ];

  // 1) When the user stops typing for 500ms, actually apply the search:
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setSearch(searchInput);
      setPage(0); // Reset to first page on new search
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchInput]);

  // 2) Reset to page 0 when sort changes
  useEffect(() => {
    setPage(0);
  }, [sort]);

  return {
    users,
    totalUsers,
    columns,
    page,
    setPage,
    pageSize,
    setPageSize,
    search,
    setSearch,
    searchInput,
    setSearchInput,
    sort,
    setSort,
    backgroundAlt,
    primaryLight,
    primaryDark,
    welcomeEmailDialogOpen,
    setWelcomeEmailDialogOpen,
    selectedUserEmail,
    selectedUserName,
    handleSendWelcomeEmail,
    passwordResetDialogOpen,
    setPasswordResetDialogOpen,
    handleSendPasswordReset,
  };
};
